import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { serviceRequestCategoriesToken } from '../../app.config';
import { UtilityService, ServiceRequestCodeOptionWithStatus } from '../../shared/utility.service';
import { ErrorService } from '../../shared/error.service';
import { EmphasisDirective } from '../../shared/directives/emphasis.directive';

export interface ServiceRequestSelectorDialogData {
    /**
     * Which field to show: 'category' or 'code'
     * - 'category': Show only category selector
     * - 'code': Show only service request code selector
     * - 'both': Show both category and code selectors
     */
    field: 'category' | 'code' | 'both';

    /**
     * Optional: Pre-selected category (useful when field = 'code')
     */
    selectedCategory?: string;

    /**
     * Optional: Initial value to pre-populate the form
     */
    initialValue?: any;

    /**
     * Optional: Custom title for the dialog
     */
    title?: string;

    /**
     * Optional: Custom hint text
     */
    hint?: string;
}

@Component({
    selector: 'app-service-request-selector-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatIconModule,
        EmphasisDirective
    ],
    templateUrl: './service-request-selector-dialog.component.html',
    styleUrl: './service-request-selector-dialog.component.scss'
})
export class ServiceRequestSelectorDialogComponent implements OnInit {
    private fb = inject(FormBuilder);
    private serviceCategories = inject(serviceRequestCategoriesToken);
    private utilityService = inject(UtilityService);
    private errorService = inject(ErrorService);

    dialogRef = inject(MatDialogRef<ServiceRequestSelectorDialogComponent>);
    data = inject<ServiceRequestSelectorDialogData>(MAT_DIALOG_DATA);

    // Form control
    selectorForm = this.fb.group({
        category: [''],
        serviceRequestCode: ['']
    });

    // Autocomplete options
    categoryOptions: any[] = [];
    serviceRequestOptions: any[] = [];
    filteredCategoryOptions: any[] = [];
    filteredServiceRequestOptions: any[] = [];

    // Loading states
    loadingCategories = false;
    loadingServiceRequests = false;

    // Track selected category for filtering codes
    selectedCategory: any = null;

    // UI computed properties
    get showCategoryField(): boolean {
        return this.data.field === 'category' || this.data.field === 'both';
    }

    get showCodeField(): boolean {
        return this.data.field === 'code' || this.data.field === 'both';
    }

    get dialogTitle(): string {
        if (this.data.title) return this.data.title;

        switch (this.data.field) {
            case 'category':
                return 'Select Service Request Category';
            case 'code':
                return 'Select Service Request Code';
            case 'both':
                return 'Select Service Request';
            default:
                return 'Select Service Request';
        }
    }

    get dialogHint(): string {
        if (this.data.hint) return this.data.hint;

        switch (this.data.field) {
            case 'category':
                return 'Search and select a category for your service request';
            case 'code':
                return 'Search and select a specific service request code';
            case 'both':
                return 'Select category and service request code';
            default:
                return '';
        }
    }

    ngOnInit(): void {
        // Initialize categories
        this.categoryOptions = [...this.serviceCategories];
        this.filteredCategoryOptions = [...this.serviceCategories];

        // Set validators based on which fields are shown
        if (this.showCategoryField) {
            this.selectorForm.get('category')?.setValidators([Validators.required]);
        }
        if (this.showCodeField) {
            this.selectorForm.get('serviceRequestCode')?.setValidators([Validators.required]);
        }
        this.selectorForm.updateValueAndValidity();


        // Pre-select category if provided (useful for code-only dialogs)
        if (this.data.selectedCategory && !this.showCategoryField) {
            this.selectedCategory = this.data.selectedCategory;
            this.searchServiceRequestCode(''); // Load codes for this category
        }
        // Pre-populate if initial values provided
        if (this.data.initialValue) {
            if (this.showCategoryField && this.data.initialValue.category) {
                this.selectorForm.patchValue({ category: this.data.initialValue.category });
            }
            if (this.showCodeField && this.data.initialValue.code) {
                this.selectorForm.patchValue({ serviceRequestCode: this.data.initialValue.code });
            }
        }
        this.selectorForm.updateValueAndValidity();

        // Setup category search with debouncing
        // if (this.showCategoryField) {
        this.selectorForm.get('category')?.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(value => {
            if (typeof value === 'string' && value.trim()) {
                this.searchCategory(value);
            } else if (value && typeof value === 'object') {
                this.onCategorySelected(value);
            }
        });
        // }

        // Setup service request code search with debouncing
        if (this.showCodeField) {
// $#$            // /* alert('setting up service request code valueChanges'); */
            this.selectorForm.get('serviceRequestCode')?.valueChanges.pipe(
                debounceTime(300),
                distinctUntilChanged()
            ).subscribe(
                (value: any) => {
// $#$                    //   /* alert('value: ' + JSON.stringify(value) */);
                    if (typeof value === 'string') {
                        this.searchServiceRequestCode(value);
                    } else if (value && typeof value === 'object') {
                        // Handle selection if needed
                        if (value.display || value.code) {
                            this.searchServiceRequestCode(value.display || value.code);

                            console.log('Service Request Code selected:', value);
                        }

                    }
                });
        }


    }

    /**
     * Called when category is selected from autocomplete
     */
    onCategorySelected(category: any): void {
        console.log('Category selected:', category);
        this.selectedCategory = category.code;

        // If showing both fields, clear and reload service request codes
        if (this.showCodeField) {
            this.serviceRequestOptions = [];
            this.filteredServiceRequestOptions = [];
            this.selectorForm.get('serviceRequestCode')?.setValue('');
            this.searchServiceRequestCode('');
        }
    }

    /**
     * Search for service request categories (client-side filtering)
     */
    searchCategory(searchTerm: string): void {
        const term = searchTerm.toLowerCase().trim();

        if (!term) {
            this.filteredCategoryOptions = [...this.categoryOptions];
            return;
        }

        this.filteredCategoryOptions = this.categoryOptions.filter((category: any) => {
            const display = category.display?.toLowerCase() || '';
            const code = category.code?.toLowerCase() || '';
            return display.includes(term) || code.includes(term);
        });
    }

    /**
     * Search for service request codes from backend
     */
    searchServiceRequestCode(searchTerm: string): void {
        this.loadingServiceRequests = true;

        this.utilityService.searchServiceRequestCodeFromCodeSystem(searchTerm, this.selectedCategory?.code || this.selectedCategory?.display ||
            this.selectedCategory.text || this.selectedCategory
        ).subscribe({
            next: (options: ServiceRequestCodeOptionWithStatus[]) => {
                this.serviceRequestOptions = options;
                this.filteredServiceRequestOptions = options;
                this.loadingServiceRequests = false;
            },
            error: (err) => {
                console.error('Error searching service request codes:', err);
                this.loadingServiceRequests = false;
                this.errorService.openandCloseError('Failed to search service request codes');
            }
        });
    }

    /**
     * Display function for autocomplete
     */
    displayOption(option: any): string {
// $#$        // /* alert('option: ' + JSON.stringify(option) */);
        return option?.display || '';
    }

    /**
     * TrackBy function for performance optimization
     */
    trackByCode(index: number, item: any): string {
        return item?.code || index;
    }

    /**
     * Save and close dialog with selected value(s)
     */
    save(): void {
        if (!this.selectorForm.valid) {
            this.errorService.openandCloseError('Please select all required fields');
            return;
        }

        const result: any = {};

        // Add category if shown and selected
        if (this.showCategoryField) {
            const categoryValue = this.selectorForm.get('category')?.value;
            if (categoryValue) {
                result.category = categoryValue;
            }
        }

        // Add code if shown and selected
        if (this.showCodeField) {
            const codeValue = this.selectorForm.get('serviceRequestCode')?.value;
            if (codeValue) {
                result.code = codeValue;
            }
        }

        // Close dialog with result
        this.dialogRef.close(result);
    }

    /**
     * Cancel and close dialog without saving
     */
    cancel(): void {
        this.dialogRef.close();
    }
}
